import { useMutation, useQuery } from "@tanstack/react-query";
import { SUPABASE_FUNCTIONS_URL, SUPABASE_ANON_KEY } from "@/lib/supabase";

/**
 * Pre-fill payload from `get-extra-form`. WP3 (SEC-H1): the edge function no
 * longer echoes `personal_number`/`bank_account` — the statist always re-enters
 * those. Only non-sensitive fields are returned and pre-filled.
 */
/** VIB-1282: kontraktmetadata for purpose='contract_signing'-tokens. */
export interface ExtraFormContractData {
  status: "utkast" | "sendt" | "signert" | "annullert";
  title: string;
  pdf_url: string | null;
}

export interface ExtraFormData {
  full_name: string;
  email: string;
  address: string;
  project_name: string;
  contract?: ExtraFormContractData;
}

export interface SubmitExtraFormInput {
  token: string;
  full_name: string;
  address: string;
  personal_number: string;
  bank_account: string;
}

export interface SignExtraContractInput {
  token: string;
  signed_name: string;
}

/**
 * Henter et eksisterende ekstra-skjema basert på et engangs-token. Brukes
 * av `ExtraFormPage`, som er en uauthentisert side eksterne medarbeidere
 * får tilsendt på e-post for å registrere personalia.
 *
 * Bruker `fetch` direkte mot edge-functionen i stedet for supabase-client
 * fordi siden ikke har en auth-session (token er det eneste credentialet).
 * Det er fortsatt en hook for at supabase-importen ikke skal lekke ut
 * i siden (CLAUDE.md regel 4) og for å samle network-logikken.
 */
export function useExtraFormToken(token: string | undefined) {
  return useQuery<ExtraFormData, Error>({
    queryKey: ["extra-form-token", token],
    queryFn: async () => {
      if (!token) throw new Error("Ugyldig lenke");
      const url = `${SUPABASE_FUNCTIONS_URL}/get-extra-form?token=${token}`;
      const res = await fetch(url, {
        headers: {
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
        },
      });
      const body = await res.json();
      if (!res.ok) {
        throw new Error(body.error ?? "Noe gikk galt");
      }
      return body as ExtraFormData;
    },
    enabled: !!token,
    retry: false,
  });
}

/**
 * Submitter et ekstra-skjema. Edge-functionen verifiserer token og lagrer
 * personalia. Token forbrukes — videre kall med samme token vil feile.
 */
export function useSubmitExtraForm() {
  return useMutation<void, Error, SubmitExtraFormInput>({
    mutationFn: async (input) => {
      const url = `${SUPABASE_FUNCTIONS_URL}/submit-extra-form`;
      const res = await fetch(url, {
        method: "POST",
        headers: {
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(input),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body.error ?? "Kunne ikke sende skjema. Prøv igjen.");
      }
    },
  });
}

/**
 * VIB-1282: signerer kontrakten knyttet til et purpose='contract_signing'-token.
 * Samme uautentiserte fetch-mønster som useSubmitExtraForm — token ER
 * autorisasjonen, ingen supabase-session finnes på denne siden.
 */
export function useSignExtraContract() {
  return useMutation<void, Error, SignExtraContractInput>({
    mutationFn: async (input) => {
      const url = `${SUPABASE_FUNCTIONS_URL}/sign-extra-contract`;
      const res = await fetch(url, {
        method: "POST",
        headers: {
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ token: input.token, signed_name: input.signed_name, confirm: true }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body.error ?? "Kunne ikke signere kontrakten. Prøv igjen.");
      }
    },
  });
}
